// ============================================================================
// DYNAMIC INDUSTRY SOLUTION DASHBOARD TYPES
// Configuration-driven dashboard system for all industry solutions
// Date: January 2026
// ============================================================================

import type { LucideIcon } from 'lucide-react';

// ============================================================================
// COLOR & THEMING
// ============================================================================

export type DashboardColorScheme = 
  | 'green' | 'blue' | 'amber' | 'red' | 'purple' 
  | 'indigo' | 'emerald' | 'orange' | 'teal' | 'cyan'
  | 'pink' | 'rose' | 'slate' | 'gray' | 'yellow';

export interface ColorConfig {
  bg: string;
  bgDark: string;
  border: string;
  borderDark: string;
  text: string;
  textDark: string;
  icon: string;
  iconDark: string;
  accent: string;
}

export const DASHBOARD_COLORS: Record<DashboardColorScheme, ColorConfig> = {
  green: { 
    bg: 'bg-green-50', bgDark: 'dark:bg-green-900/30', 
    border: 'border-green-200', borderDark: 'dark:border-green-800', 
    text: 'text-green-700', textDark: 'dark:text-green-400', 
    icon: 'text-green-600', iconDark: 'dark:text-green-400', 
    accent: '#10b981' 
  },
  blue: { 
    bg: 'bg-blue-50', bgDark: 'dark:bg-blue-900/30', 
    border: 'border-blue-200', borderDark: 'dark:border-blue-800', 
    text: 'text-blue-700', textDark: 'dark:text-blue-400', 
    icon: 'text-blue-600', iconDark: 'dark:text-blue-400', 
    accent: '#3b82f6' 
  },
  amber: { 
    bg: 'bg-amber-50', bgDark: 'dark:bg-amber-900/30', 
    border: 'border-amber-200', borderDark: 'dark:border-amber-800', 
    text: 'text-amber-700', textDark: 'dark:text-amber-400', 
    icon: 'text-amber-600', iconDark: 'dark:text-amber-400', 
    accent: '#f59e0b' 
  },
  red: { 
    bg: 'bg-red-50', bgDark: 'dark:bg-red-900/30', 
    border: 'border-red-200', borderDark: 'dark:border-red-800', 
    text: 'text-red-700', textDark: 'dark:text-red-400', 
    icon: 'text-red-600', iconDark: 'dark:text-red-400', 
    accent: '#ef4444' 
  },
  purple: { 
    bg: 'bg-purple-50', bgDark: 'dark:bg-purple-900/30', 
    border: 'border-purple-200', borderDark: 'dark:border-purple-800', 
    text: 'text-purple-700', textDark: 'dark:text-purple-400', 
    icon: 'text-purple-600', iconDark: 'dark:text-purple-400', 
    accent: '#8b5cf6' 
  },
  indigo: { 
    bg: 'bg-indigo-50', bgDark: 'dark:bg-indigo-900/30', 
    border: 'border-indigo-200', borderDark: 'dark:border-indigo-800', 
    text: 'text-indigo-700', textDark: 'dark:text-indigo-400', 
    icon: 'text-indigo-600', iconDark: 'dark:text-indigo-400', 
    accent: '#6366f1' 
  },
  emerald: { 
    bg: 'bg-emerald-50', bgDark: 'dark:bg-emerald-900/30', 
    border: 'border-emerald-200', borderDark: 'dark:border-emerald-800', 
    text: 'text-emerald-700', textDark: 'dark:text-emerald-400', 
    icon: 'text-emerald-600', iconDark: 'dark:text-emerald-400', 
    accent: '#059669' 
  },
  orange: { 
    bg: 'bg-orange-50', bgDark: 'dark:bg-orange-900/30', 
    border: 'border-orange-200', borderDark: 'dark:border-orange-800', 
    text: 'text-orange-700', textDark: 'dark:text-orange-400', 
    icon: 'text-orange-600', iconDark: 'dark:text-orange-400', 
    accent: '#ea580c' 
  },
  teal: { 
    bg: 'bg-teal-50', bgDark: 'dark:bg-teal-900/30', 
    border: 'border-teal-200', borderDark: 'dark:border-teal-800', 
    text: 'text-teal-700', textDark: 'dark:text-teal-400', 
    icon: 'text-teal-600', iconDark: 'dark:text-teal-400', 
    accent: '#14b8a6' 
  },
  cyan: { 
    bg: 'bg-cyan-50', bgDark: 'dark:bg-cyan-900/30', 
    border: 'border-cyan-200', borderDark: 'dark:border-cyan-800', 
    text: 'text-cyan-700', textDark: 'dark:text-cyan-400', 
    icon: 'text-cyan-600', iconDark: 'dark:text-cyan-400', 
    accent: '#06b6d4' 
  },
  pink: { 
    bg: 'bg-pink-50', bgDark: 'dark:bg-pink-900/30', 
    border: 'border-pink-200', borderDark: 'dark:border-pink-800', 
    text: 'text-pink-700', textDark: 'dark:text-pink-400', 
    icon: 'text-pink-600', iconDark: 'dark:text-pink-400', 
    accent: '#ec4899' 
  },
  rose: { 
    bg: 'bg-rose-50', bgDark: 'dark:bg-rose-900/30', 
    border: 'border-rose-200', borderDark: 'dark:border-rose-800', 
    text: 'text-rose-700', textDark: 'dark:text-rose-400', 
    icon: 'text-rose-600', iconDark: 'dark:text-rose-400', 
    accent: '#f43f5e' 
  },
  slate: { 
    bg: 'bg-slate-50', bgDark: 'dark:bg-slate-900/30', 
    border: 'border-slate-200', borderDark: 'dark:border-slate-800', 
    text: 'text-slate-700', textDark: 'dark:text-slate-400', 
    icon: 'text-slate-600', iconDark: 'dark:text-slate-400', 
    accent: '#64748b' 
  },
  gray: { 
    bg: 'bg-gray-50', bgDark: 'dark:bg-gray-900/30', 
    border: 'border-gray-200', borderDark: 'dark:border-gray-800', 
    text: 'text-gray-700', textDark: 'dark:text-gray-400', 
    icon: 'text-gray-600', iconDark: 'dark:text-gray-400', 
    accent: '#6b7280' 
  },
  yellow: { 
    bg: 'bg-yellow-50', bgDark: 'dark:bg-yellow-900/30', 
    border: 'border-yellow-200', borderDark: 'dark:border-yellow-800', 
    text: 'text-yellow-700', textDark: 'dark:text-yellow-400', 
    icon: 'text-yellow-600', iconDark: 'dark:text-yellow-400', 
    accent: '#eab308' 
  },
};

// ============================================================================
// KPI DEFINITIONS
// ============================================================================

export type KPIAggregation = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'percent' | 'custom';
export type KPITrendDirection = 'up_good' | 'down_good' | 'neutral';

export interface KPIDefinition {
  id: string;
  label: string;
  description?: string;
  icon: string; // Lucide icon name
  color: DashboardColorScheme;
  
  // Data source
  itemTypes: string[]; // Filter work_items by these types
  itemSubtype?: string; // Filter by item subtype
  statusFilter?: string[]; // Filter by status
  dateField?: string; // Field to use for date filtering (e.g., 'created_at', 'due_date')
  
  // Aggregation
  aggregation: KPIAggregation;
  field?: string; // Field to aggregate (for sum/avg/min/max)
  customFormula?: string; // For complex calculations
  
  // Display
  format: 'number' | 'currency' | 'percent' | 'duration' | 'time';
  prefix?: string;
  suffix?: string;
  decimals?: number;
  
  // Trend
  showTrend?: boolean;
  trendDirection?: KPITrendDirection;
  trendPeriod?: 'day' | 'week' | 'month';
  
  // Targets
  target?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  
  // Click action
  onClick?: {
    type: 'navigate' | 'filter' | 'modal';
    target: string;
  };
}

// ============================================================================
// CHART DEFINITIONS
// ============================================================================

export type ChartType = 
  | 'bar' | 'line' | 'area' | 'pie' | 'donut' 
  | 'radial' | 'gauge' | 'composed' | 'stacked_bar' 
  | 'treemap' | 'funnel' | 'heatmap';

export interface ChartDataSource {
  itemTypes: string[];
  groupBy: string; // Field to group by (e.g., 'status', 'item_type', 'assigned_to')
  aggregation: KPIAggregation;
  field?: string;
  sortBy?: 'value' | 'label' | 'date';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  dateRange?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  statusFilter?: string[];
}

export interface ChartDefinition {
  id: string;
  title: string;
  description?: string;
  type: ChartType;
  dataSource: ChartDataSource;
  
  // Layout
  gridSpan?: 1 | 2 | 3 | 4; // columns in grid (1-4)
  height?: 'sm' | 'md' | 'lg'; // 200px, 300px, 400px
  
  // Colors
  colors?: string[];
  colorField?: string; // Field to derive color from
  
  // Axes (for bar/line/area)
  xAxisLabel?: string;
  yAxisLabel?: string;
  
  // Legend
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  
  // Interactive
  onClick?: {
    type: 'navigate' | 'filter' | 'drill_down';
    target: string;
  };

  // YAML catalog fields (used when chart config comes from YAML template)
  series?: unknown;
  xAxis?: unknown;
  groupBy?: unknown;
  showCount?: boolean;
  limit?: number;
}

// ============================================================================
// QUICK ACTION DEFINITIONS
// ============================================================================

export interface QuickActionDefinition {
  id: string;
  label: string;
  description?: string;
  icon: string;
  color: DashboardColorScheme;
  
  // Action type
  action: {
    type: 'create_work_item' | 'navigate' | 'modal' | 'external_link';
    itemType?: string;
    itemSubtype?: string;
    target?: string;
    url?: string;
  };
  
  // Visibility
  requiresPermission?: 'can_create' | 'can_edit' | 'can_admin';
  showForTemplates?: string[]; // Only show if these templates are enabled
}

// ============================================================================
// WIDGET DEFINITIONS
// ============================================================================

export type WidgetType = 
  | 'kpi_grid' | 'chart' | 'quick_actions' | 'recent_items' 
  | 'progress_ring' | 'stats_summary' | 'alerts' | 'activity_feed'
  | 'team_performance' | 'calendar_preview' | 'map_preview';

export interface WidgetDefinition {
  id: string;
  type: WidgetType;
  title?: string;
  
  // Layout
  gridColumn?: string; // e.g., 'span 2', '1 / 3'
  gridRow?: string;
  
  // Data
  kpis?: string[]; // KPI IDs for kpi_grid
  chart?: string; // Chart ID
  actions?: string[]; // Action IDs
  
  // Conditional rendering
  showForTemplates?: string[];
  requiresPermission?: string;
}

// ============================================================================
// DASHBOARD LAYOUT
// ============================================================================

export interface DashboardSection {
  id: string;
  title?: string;
  description?: string;
  
  // Layout
  columns?: 1 | 2 | 3 | 4 | 6;
  gap?: 'sm' | 'md' | 'lg';
  
  // Widgets in this section
  widgets: WidgetDefinition[];
  
  // Conditional rendering
  showForTemplates?: string[];
  collapsed?: boolean;
  collapsible?: boolean;
}

export interface DashboardLayout {
  header: {
    showLogo?: boolean;
    showPeriodSelector?: boolean;
    showRefresh?: boolean;
    showExport?: boolean;
    showSettings?: boolean;
    quickStats?: string[]; // KPI IDs to show in header
  };
  sections: DashboardSection[];
}

// ============================================================================
// COMPLETE DASHBOARD CONFIGURATION
// ============================================================================

export interface IndustrySolutionDashboardConfig {
  // Identity
  solutionKey: string;
  dashboardKey: string;
  title: string;
  subtitle?: string;
  icon: string;
  primaryColor: string;
  
  // Item types this dashboard tracks
  itemTypes: string[];
  
  // KPI definitions
  kpis: KPIDefinition[];
  
  // Chart definitions
  charts: ChartDefinition[];
  
  // Quick actions
  quickActions: QuickActionDefinition[];
  
  // Status groups for grouping statuses in status distribution (from DB config)
  statusGroups?: Array<{
    key: string;
    label: string;
    color?: string;
    statuses: string[];
  }>;
  
  // Layout
  layout: DashboardLayout;
  
  // Active templates (from client config)
  activeTemplates: string[];
  
  // Additional config
  refreshInterval?: number; // Auto-refresh in seconds
  exportFormats?: ('pdf' | 'excel' | 'csv')[];
}

// ============================================================================
// COMPUTED DASHBOARD DATA (from hooks)
// ============================================================================

export interface ComputedKPI {
  id: string;
  label: string;
  value: number | string;
  formattedValue: string;
  icon: string;
  color: DashboardColorScheme;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'stable';
    isGood: boolean;
  };
  target?: {
    value: number;
    percentage: number;
    status: 'on_track' | 'warning' | 'critical';
  };
  onClick?: () => void;
}

export interface DashboardItem {
  id?: string | number;
  item_type?: string;
  status?: string;
  due_date?: string | null;
  created_at?: string;
  priority?: string;
  title?: string;
  item_number?: string;
  [key: string]: unknown;
}

export interface ComputedChartData {
  id: string;
  title: string;
  type: ChartType;
  data: Array<Record<string, unknown>>;
  colors: string[];
  config: ChartDefinition;
}

export interface DashboardData {
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Computed values
  kpis: ComputedKPI[];
  charts: ComputedChartData[];
  
  // Raw work items
  items: DashboardItem[];
  itemsByType: Record<string, DashboardItem[]>;
  itemsByStatus: Record<string, DashboardItem[]>;
  
  // Actions
  refresh: () => Promise<void>;
}

// ============================================================================
// INDUSTRY-SPECIFIC KPI PRESETS
// ============================================================================

// These can be extended per industry in the database
export const COMMON_KPI_PRESETS: Record<string, Partial<KPIDefinition>> = {
  total_items: {
    label: 'Total Items',
    aggregation: 'count',
    format: 'number',
    icon: 'Package',
    color: 'blue',
  },
  active_items: {
    label: 'Active',
    aggregation: 'count',
    statusFilter: ['open', 'in_progress', 'pending', 'scheduled'],
    format: 'number',
    icon: 'Activity',
    color: 'green',
  },
  completed_items: {
    label: 'Completed',
    aggregation: 'count',
    statusFilter: ['completed', 'done', 'closed'],
    format: 'number',
    icon: 'CheckCircle',
    color: 'emerald',
  },
  overdue_items: {
    label: 'Overdue',
    aggregation: 'count',
    format: 'number',
    icon: 'AlertTriangle',
    color: 'red',
    trendDirection: 'down_good',
  },
  total_revenue: {
    label: 'Total Revenue',
    aggregation: 'sum',
    field: 'grand_total',
    format: 'currency',
    icon: 'DollarSign',
    color: 'green',
  },
  average_completion_time: {
    label: 'Avg. Completion',
    aggregation: 'avg',
    format: 'duration',
    icon: 'Clock',
    color: 'blue',
  },
  on_time_rate: {
    label: 'On-Time Rate',
    aggregation: 'percent',
    format: 'percent',
    icon: 'Target',
    color: 'green',
    target: 95,
  },
  sla_compliance: {
    label: 'SLA Compliance',
    aggregation: 'percent',
    format: 'percent',
    icon: 'Shield',
    color: 'purple',
    target: 98,
  },
};

// ============================================================================
// ICON MAPPING
// ============================================================================

import {
  Package, Activity, CheckCircle, AlertTriangle, DollarSign, Clock,
  Target, Shield, Users, Truck, Wrench, Factory, Recycle, Leaf,
  Building2, MapPin, Calendar, FileText, TrendingUp, TrendingDown,
  BarChart3, Briefcase, Code, Headset, Hammer, Box, Gauge, Star,
  Award, ClipboardCheck, Eye, RefreshCw, Download, Settings, Plus,
  ArrowUp, ArrowDown, Minus, ChevronRight, Filter, Zap, Timer,
  Phone, Route, Scale, Hash, Layout, TreePine, Ticket, Receipt,
  Globe, Edit2, Play, Pause, AlertCircle, FileCheck, Layers,
  GitBranch, Rocket, ShoppingCart, Heart, HardHat, Stethoscope,
  Music, Lightbulb, Scissors, Brush, Anchor, Plane, Home, Car,
  Radio, Wheat, Mountain, Server, Gavel, Flame, Droplets, Wind,
  Thermometer, PlugZap, Pipette, ShieldCheck, Sprout, BadgeCheck,
  Megaphone,
} from 'lucide-react';

export const DASHBOARD_ICON_MAP: Record<string, LucideIcon> = {
  Package, Activity, CheckCircle, AlertTriangle, DollarSign, Clock,
  Target, Shield, Users, Truck, Wrench, Factory, Recycle, Leaf,
  Building2, MapPin, Calendar, FileText, TrendingUp, TrendingDown,
  BarChart3, Briefcase, Code, Headset, Hammer, Box, Gauge, Star,
  Award, ClipboardCheck, Eye, RefreshCw, Download, Settings, Plus,
  ArrowUp, ArrowDown, Minus, ChevronRight, Filter, Zap, Timer,
  Phone, Route, Scale, Hash, Layout, TreePine, Ticket, Receipt,
  Globe, Edit2, Play, Pause, AlertCircle, FileCheck, Layers,
  GitBranch, Rocket, ShoppingCart, Heart, HardHat, Stethoscope,
  Music, Lightbulb, Scissors, Brush, Anchor, Plane, Home, Car,
  Radio, Wheat, Mountain, Server, Gavel, Flame, Droplets, Wind,
  Thermometer, PlugZap, Pipette, ShieldCheck, Sprout, BadgeCheck,
  Megaphone,
};

export function getIconComponent(iconName: string): LucideIcon {
  return DASHBOARD_ICON_MAP[iconName] || Package;
}
